// Modal "Pedir análise à transportadora" (PO1-3391). Abre com a prévia
// (POST /ctes/pedir-analise/preview): um bloco por transportadora com os CT-es,
// os destinatários sugeridos (editáveis), um chip do acesso ao portal e, no
// fim, uma mensagem opcional. Enviar (POST /ctes/pedir-analise) mostra o
// resultado por transportadora no próprio modal. Nunca mostra valores: a
// divergência fica entre o cliente e a transportadora, dentro do portal.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, ModalBody } from "reactstrap";
import { ClipLoader } from "react-spinners";
import {
  AUTOPILOT_COLORS,
  autopilotEyebrowStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";
import { rotuloDocumentos } from "../../../../utils/modeloDocumento";
import {
  previewPedidoAnalise,
  enviarPedidoAnalise,
  mensagemErro,
  ROTULO_ACESSO,
} from "../../../../services/pedidoAnaliseCteApi";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MAX_CTES_POR_ENVIO = 500;
const MAX_MENSAGEM = 1000;

const closeButtonStyle = {
  border: 0,
  background: "transparent",
  color: "var(--everlog-card-text)",
  fontSize: "2rem",
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
  opacity: 0.95,
  textShadow: "none",
};

const blocoStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  background: AUTOPILOT_COLORS.insetSurface,
  borderRadius: 10,
  padding: "0.75rem 0.9rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const TONE_COLOR = {
  success: "var(--everlog-status-success)",
  info: "var(--everlog-status-info, #1d8cf8)",
  warning: "var(--everlog-status-warning, #ff8d72)",
  danger: "var(--everlog-status-danger)",
};

function chip(tone, texto) {
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "0.72rem",
        fontWeight: 700,
        padding: "0.15rem 0.55rem",
        borderRadius: 999,
        border: `1px solid ${TONE_COLOR[tone] || AUTOPILOT_COLORS.border}`,
        color: TONE_COLOR[tone] || AUTOPILOT_COLORS.text,
      }}
    >
      {texto}
    </span>
  );
}

// "a@x.com, b@y.com; c@z.com" → ["a@x.com", "b@y.com", "c@z.com"] (minúsculas, sem duplicata).
export function parseEmails(texto) {
  const vistos = new Set();
  const out = [];
  String(texto || "")
    .split(/[,;\s]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
    .forEach((e) => {
      if (!vistos.has(e)) {
        vistos.add(e);
        out.push(e);
      }
    });
  return out;
}

export function emailsInvalidos(texto) {
  return parseEmails(texto).filter((e) => !EMAIL_REGEX.test(e));
}

export default function PedirAnaliseModal({ isOpen, onClose, embarcadorId, ids, itens, onEnviado }) {
  const [carregando, setCarregando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [preview, setPreview] = useState(null); // { ignorados, grupos, solicitante }
  const [destinatarios, setDestinatarios] = useState({}); // idTransportadora → texto
  const [mensagem, setMensagem] = useState("");
  const [resultado, setResultado] = useState(null); // { ignorados, resultados }

  const rotulo = useMemo(() => rotuloDocumentos(itens || []), [itens]);

  useEffect(() => {
    let cancelado = false;
    async function carregar() {
      if (!isOpen) return;
      setCarregando(true);
      setErro(null);
      setPreview(null);
      setResultado(null);
      setMensagem("");
      try {
        const data = await previewPedidoAnalise({ embarcadorId, ids });
        if (cancelado) return;
        setPreview(data);
        const iniciais = {};
        (data.grupos || []).forEach((g) => {
          iniciais[g.idTransportadora] = (g.destinatarios || []).join(", ");
        });
        setDestinatarios(iniciais);
      } catch (err) {
        if (!cancelado) setErro(mensagemErro(err, "Não foi possível preparar o pedido."));
      } finally {
        if (!cancelado) setCarregando(false);
      }
    }
    carregar();
    return () => {
      cancelado = true;
    };
  }, [isOpen, embarcadorId, ids]);

  const grupos = (preview && preview.grupos) || [];
  const problemas = useMemo(() => {
    const p = {};
    grupos.forEach((g) => {
      const texto = destinatarios[g.idTransportadora] || "";
      const lista = parseEmails(texto);
      const invalidos = emailsInvalidos(texto);
      if (!lista.length) p[g.idTransportadora] = "Informe um e-mail para enviar.";
      else if (invalidos.length) p[g.idTransportadora] = `E-mail inválido: ${invalidos.join(", ")}`;
    });
    return p;
  }, [grupos, destinatarios]);
  const podeEnviar = grupos.length > 0 && Object.keys(problemas).length === 0 && !enviando && !resultado;

  const handleEnviar = useCallback(async () => {
    if (!podeEnviar) return;
    setEnviando(true);
    setErro(null);
    try {
      const dest = {};
      grupos.forEach((g) => {
        dest[g.idTransportadora] = parseEmails(destinatarios[g.idTransportadora]);
      });
      const data = await enviarPedidoAnalise({ embarcadorId, ids, mensagem: mensagem.trim() || null, destinatarios: dest });
      setResultado(data);
      if (typeof onEnviado === "function") onEnviado(data);
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível enviar o pedido."));
    } finally {
      setEnviando(false);
    }
  }, [podeEnviar, grupos, destinatarios, embarcadorId, ids, mensagem, onEnviado]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      modalClassName="modal-align-top"
      backdropClassName="cte-modal-backdrop"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.8rem",
          padding: "1rem 1.25rem 0.75rem",
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={autopilotEyebrowStyle}>Pedir análise à transportadora</div>
          <div style={{ ...autopilotTitleStyle, fontSize: "1.1rem", marginBottom: "0.15rem" }}>
            {ids.length} {rotulo} selecionado{ids.length === 1 ? "" : "s"}
          </div>
          <div style={{ fontSize: "0.8rem", color: AUTOPILOT_COLORS.muted }}>
            A transportadora recebe um e-mail por empresa pedindo a explicação do cálculo, com o link para os CT-es no portal.
            O valor pela nossa tabela e a diferença não são mostrados a ela.
            Quem pediu fica em cópia e recebe a resposta.
          </div>
        </div>
        <button type="button" aria-label="Fechar" onClick={onClose} style={closeButtonStyle}>
          ×
        </button>
      </div>
      <ModalBody
        style={{
          background: "var(--everlog-card-surface)",
          color: AUTOPILOT_COLORS.text,
          padding: "1rem 1.25rem 1.25rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.8rem",
        }}
      >
        {carregando ? (
          <div style={{ textAlign: "center", padding: "1rem" }}>
            <ClipLoader size={28} color="#1d8cf8" loading />
          </div>
        ) : null}

        {erro ? (
          <div data-testid="pedir-analise-erro" style={{ color: "var(--everlog-status-danger)", fontWeight: 600, fontSize: "0.82rem" }}>
            {erro}
          </div>
        ) : null}

        {!carregando && preview && preview.ignorados > 0 ? (
          <div style={{ fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted }}>
            {preview.ignorados} {rotulo} ignorado(s): fora da fila "Com divergência" ou fora do seu escopo.
          </div>
        ) : null}

        {!carregando && preview && grupos.length === 0 ? (
          <div style={{ fontSize: "0.85rem" }}>Nenhum {rotulo} elegível na seleção.</div>
        ) : null}

        {!carregando && resultado ? (
          <div data-testid="pedir-analise-resultado" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {(resultado.resultados || []).map((r) => (
              <div key={r.idTransportadora} style={{ ...blocoStyle, flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{r.transportadora || `Transportadora ${r.idTransportadora}`}</div>
                  <div style={{ fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted }}>
                    {r.qtd} {rotulo} · {(r.destinatarios || []).join(", ") || "sem destinatário"}
                  </div>
                </div>
                <div>
                  {r.enviado
                    ? chip("success", "✓ Enviado")
                    : chip("danger", r.erro === "sem_destinatario" ? "Sem destinatário" : `Falhou: ${r.erro}`)}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button color="info" className="btn-sm" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </div>
        ) : null}

        {!carregando && preview && grupos.length > 0 && !resultado ? (
          <>
            {grupos.map((g) => {
              const acesso = ROTULO_ACESSO[g.acesso] || { texto: g.acesso || "—", tone: "info" };
              const problema = problemas[g.idTransportadora];
              const primeiro = parseEmails(destinatarios[g.idTransportadora])[0];
              return (
                <div key={g.idTransportadora} style={blocoStyle} data-testid={`grupo-${g.idTransportadora}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 700 }}>
                      {g.transportadora || `Transportadora ${g.idTransportadora}`}{" "}
                      <span style={{ fontWeight: 400, color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" }}>
                        · {g.ctes.length} {rotulo}
                      </span>
                    </div>
                    {chip(acesso.tone, acesso.texto)}
                  </div>
                  <div style={{ fontSize: "0.76rem", color: AUTOPILOT_COLORS.muted, wordBreak: "break-word" }}>
                    {g.ctes.slice(0, 20).map((c) => c.numero).join(", ")}
                    {g.ctes.length > 20 ? ` e mais ${g.ctes.length - 20}` : ""}
                  </div>
                  <div>
                    <label htmlFor={`dest-${g.idTransportadora}`} style={{ fontSize: "0.76rem", color: AUTOPILOT_COLORS.muted, marginBottom: 2 }}>
                      Destinatários (separe por vírgula)
                    </label>
                    <Input
                      id={`dest-${g.idTransportadora}`}
                      type="text"
                      bsSize="sm"
                      value={destinatarios[g.idTransportadora] || ""}
                      placeholder="email@transportadora.com.br"
                      invalid={!!problema}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDestinatarios((prev) => ({ ...prev, [g.idTransportadora]: v }));
                      }}
                    />
                    {problema ? (
                      <div style={{ fontSize: "0.74rem", color: "var(--everlog-status-danger)", marginTop: 2 }}>{problema}</div>
                    ) : null}
                    {!problema && (g.acesso === "convite_novo") && primeiro ? (
                      <div style={{ fontSize: "0.74rem", color: AUTOPILOT_COLORS.muted, marginTop: 2 }}>
                        O convite de acesso ao portal vai para <strong>{primeiro}</strong>.
                      </div>
                    ) : null}
                    {!problema && g.contatos && g.contatos.length === 0 ? (
                      <div style={{ fontSize: "0.74rem", color: AUTOPILOT_COLORS.muted, marginTop: 2 }}>
                        Sem contato cadastrado: o e-mail informado fica salvo como contato de Inconsistência.
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
            <div>
              <label htmlFor="pedir-analise-mensagem" style={{ fontSize: "0.76rem", color: AUTOPILOT_COLORS.muted, marginBottom: 2 }}>
                Mensagem para a transportadora (opcional, até {MAX_MENSAGEM} caracteres)
              </label>
              <Input
                id="pedir-analise-mensagem"
                type="textarea"
                rows={3}
                maxLength={MAX_MENSAGEM}
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                placeholder="Ex.: Favor conferir a tabela vigente para estes CT-es."
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", alignItems: "center" }}>
              {preview.solicitante && preview.solicitante.email ? (
                <span style={{ fontSize: "0.74rem", color: AUTOPILOT_COLORS.muted }}>
                  Cópia e respostas para {preview.solicitante.email}
                </span>
              ) : null}
              <Button color="secondary" className="btn-sm" onClick={onClose} disabled={enviando}>
                Cancelar
              </Button>
              <Button color="success" className="btn-sm" onClick={handleEnviar} disabled={!podeEnviar} data-testid="pedir-analise-enviar">
                {enviando ? <ClipLoader size={12} color="#fff" /> : `Enviar (${grupos.length} e-mail${grupos.length === 1 ? "" : "s"})`}
              </Button>
            </div>
          </>
        ) : null}
      </ModalBody>
    </Modal>
  );
}
